import React, { useState } from 'react';
import { BlockStack, InlineStack, Text, Badge, Box, Button, Icon } from '@shopify/polaris';
import { ViewIcon, HideIcon, TextTitleIcon } from '@shopify/polaris-icons';
import type { FixPreviewDto } from '../../types';
import './FieldDiff.css';

/**
 * T063: FieldDiff — before/after inline display with source badge.
 *
 * Shows original value, proposed value, and "⚡ AI-inferred" badge
 * when source is INFERRED (FR-002, FR-005).
 *
 * The title field keeps a read-only "After" value with a caps icon button that
 * rotates its casing (ALL CAPS / Title Case / Sentence case).
 */

/** The three title capitalization styles the caps toggle rotates through. */
const CASE_STYLES = ['uppercase', 'title-case', 'sentence-case'] as const;
type CaseStyle = (typeof CASE_STYLES)[number];

/** Button labels that demonstrate each case so the merchant sees the effect. */
const CASE_LABELS: Record<CaseStyle, string> = {
  uppercase: 'ALL CAPS',
  'title-case': 'Title Case',
  'sentence-case': 'Sentence case',
};

/** Apply a capitalization style to a string. Mirrors the backend re-caser. */
function applyCase(text: string, style: CaseStyle): string {
  switch (style) {
    case 'uppercase':
      return text.toUpperCase();
    case 'title-case':
      return text.replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    case 'sentence-case':
      return text.toLowerCase().replace(/\p{L}/u, (c) => c.toUpperCase());
  }
}

/** Detect which case style a string already matches (defaults to Title Case). */
function detectCaseIndex(text: string): number {
  const idx = CASE_STYLES.findIndex((style) => applyCase(text, style) === text);
  return idx === -1 ? 1 : idx;
}

const SOURCE_LABELS: Record<string, { label: string; tone?: 'info' | 'attention' | 'success' }> = {
  EXTRACTED: { label: 'From existing data', tone: 'success' },
  CORRECTED: { label: 'Auto-improved', tone: 'info' },
  INFERRED: { label: '⚡ AI-generated — please verify', tone: 'attention' },
};

/** Human-friendly display names matching Shopify product-edit page labels. */
const FIELD_DISPLAY_NAMES: Record<string, string> = {
  title: 'Title',
  body_html: 'Description',
  image_alt_text: 'Image alt text',
  product_type: 'Product type',
  vendor: 'Vendor',
  tags: 'Tags',
  metafields: 'Metafields',
  seo_title: 'SEO title',
  seo_description: 'SEO description',
  json_ld: 'JSON-LD schema',
};

/** Resolve a human-friendly label for any field key, including positional image alt text fields. */
function getFieldDisplayName(field: string): string {
  if (FIELD_DISPLAY_NAMES[field]) return FIELD_DISPLAY_NAMES[field];
  const match = /^image_alt_text_(\d+)$/.exec(field);
  if (match) {
    const pos = Number.parseInt(match[1], 10);
    return pos === 1 ? 'Image 1 alt text (primary)' : `Image ${pos} alt text`;
  }
  return field;
}

interface FieldDiffProps {
  readonly preview: FixPreviewDto;
  /** Current merchant edit for this field (title only); undefined = no edit yet. */
  readonly editValue?: string;
  /** Called when the merchant edits or re-cases the title inline (title only). */
  readonly onEditChange?: (value: string) => void;
}

/** Strip any script/iframe tags from HTML before rendering as a preview. */
function sanitizeHtml(html: string): string {
  return html.replaceAll(/<script[\s\S]*?<\/script>/gi, '').replaceAll(/<iframe[\s\S]*?>/gi, '');
}

/**
 * Parse the HTML through the browser's native DOMParser so that missing
 * closing tags, mis-nested elements, and other malformed markup are
 * auto-corrected before we render. This prevents broken HTML from bleeding
 * layout or styles into the surrounding Polaris UI.
 */
function normalizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(sanitizeHtml(html), 'text/html');
  return doc.body.innerHTML;
}

export function FieldDiff({ preview, editValue, onEditChange }: FieldDiffProps): React.JSX.Element {
  const sourceInfo = SOURCE_LABELS[preview.source] ?? { label: preview.source };
  const displayValue = preview.merchantEdit ?? preview.proposedValue;
  const fieldLabel = getFieldDisplayName(preview.field);
  const isHtmlField = preview.field === 'body_html';
  // The title's caps button shows only while the suggestion is still pending —
  // once approved/applied/rejected it falls back to a plain read-only display.
  const showTitleCaps = preview.field === 'title' && preview.status === 'PENDING';

  const [htmlPreviewActive, setHtmlPreviewActive] = useState(isHtmlField);

  // Title "After" stays read-only; the caps button re-cases it in place. Live
  // edit (editValue) takes precedence over the suggested/merchant value.
  const titleValue = editValue ?? displayValue;

  // Cycle to the next capitalization style that actually changes the string, so
  // the button never appears to "do nothing" (e.g. single-word titles where
  // title-case and sentence-case are identical). Stateless — the current case
  // is derived from the value each click, so it can never desync. This only
  // stages the value in the parent's edit map; nothing is saved until approval.
  const handleCycleCaps = () => {
    const current = detectCaseIndex(titleValue);
    for (let step = 1; step <= CASE_STYLES.length; step++) {
      const candidate = applyCase(titleValue, CASE_STYLES[(current + step) % CASE_STYLES.length]);
      if (candidate !== titleValue) {
        onEditChange?.(candidate);
        return;
      }
    }
  };

  const afterLabel = preview.merchantEdit ? 'Your edit:' : 'After:';
  const caseLabel = CASE_LABELS[CASE_STYLES[detectCaseIndex(titleValue)]];

  /** Render the "After" content: read-only title + caps button, HTML preview, or plain text. */
  function renderAfter(): React.JSX.Element {
    if (showTitleCaps) {
      return (
        <InlineStack gap="200" blockAlign="center" align="space-between" wrap={false}>
          <Text as="p" variant="bodyMd">{titleValue}</Text>
          <span title={`Capitalization: ${caseLabel} — tap to change`}>
            <Button
              icon={TextTitleIcon}
              variant="tertiary"
              onClick={handleCycleCaps}
              accessibilityLabel={`Change title capitalization (currently ${caseLabel})`}
            />
          </span>
        </InlineStack>
      );
    }
    if (htmlPreviewActive && displayValue) {
      return (
        <div
          className="field-diff-html-preview"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: normalizeHtml(displayValue) }}
        />
      );
    }
    return <Text as="p" variant="bodyMd">{displayValue}</Text>;
  }

  return (
    <BlockStack gap="200">
      <InlineStack align="space-between" blockAlign="center" wrap={false}>
        <InlineStack gap="200" blockAlign="center">
          <Text as="span" variant="headingSm">{fieldLabel}</Text>
          <Badge tone={sourceInfo.tone}>{sourceInfo.label}</Badge>
          {preview.confidence != null && (
            <Text as="span" variant="bodySm" tone="subdued">
              {Math.round(preview.confidence * 100)}% confidence
            </Text>
          )}
        </InlineStack>
        {isHtmlField && (
          <Button
            size="slim"
            variant="tertiary"
            icon={<Icon source={htmlPreviewActive ? HideIcon : ViewIcon} />}
            onClick={() => setHtmlPreviewActive((v) => !v)}
          >
            {htmlPreviewActive ? 'Raw' : 'Preview'}
          </Button>
        )}
      </InlineStack>

      <Box
        paddingInline="300"
        paddingBlock="200"
        borderWidth="025"
        borderColor="border"
        borderRadius="200"
        background="bg-surface-secondary"
      >
        <BlockStack gap="100">
          <Text as="p" variant="bodySm" tone="subdued">Before:</Text>
          {htmlPreviewActive && preview.originalValue ? (
            <div
              className="field-diff-html-preview"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: normalizeHtml(preview.originalValue) }}
            />
          ) : (
            <Text as="p" variant="bodyMd" tone="caution">
              {preview.originalValue || '(empty)'}
            </Text>
          )}
        </BlockStack>
      </Box>

      <div className="field-diff-after-box">
        <BlockStack gap="100">
          <Text as="p" variant="bodySm" tone="subdued">{afterLabel}</Text>
          {renderAfter()}
        </BlockStack>
      </div>
    </BlockStack>
  );
}

export default FieldDiff;
